import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  Users,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Code,
  BarChart3,
  Zap,
} from "lucide-react";

export default function WorkforceDevelopment() {
  const features = [
    {
      icon: TrendingUp,
      title: "Program-Wide Deployment",
      description: "Deploy across your entire cohort with bulk account creation, custom branding, and centralized management.",
    },
    {
      icon: BarChart3,
      title: "ROI Tracking",
      description: "Prove program effectiveness with placement rates, time-to-employment, and salary outcome tracking.",
    },
    {
      icon: Code,
      title: "API Access",
      description: "Integrate with your LMS, student information system, or custom platform via our enterprise API.",
    },
    {
      icon: Zap,
      title: "Dedicated Training",
      description: "Onboard instructors and participants with custom training programs and ongoing support resources.",
    },
  ];

  const benefits = [
    "Accelerate job placement with AI tools",
    "Real-time cohort analytics and dashboards",
    "Custom workshops and training materials",
    "Integration with existing systems",
    "Participant progress tracking",
    "Employer partnership tools",
    "Success stories and testimonials",
    "Flexible pricing for program size",
  ];

  return (
    <>
      <Helmet>
        <title>Workforce Development Solutions | LoopBaan - Career Training Program Tools</title>
        <meta
          name="description"
          content="Accelerate placement rates with LoopBaan's workforce development platform. Program deployment, ROI tracking, API access, and dedicated training support."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <Users className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Workforce Development</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Accelerate Placements
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                With AI-Powered Tools
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Give your program participants professional-grade career tools, track outcomes in real-time, and prove ROI to stakeholders and funders.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Request Program Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Program Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Program</div>
              <div className="text-gray-400 text-sm md:text-base">Deployment</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">ROI</div>
              <div className="text-gray-400 text-sm md:text-base">Tracking & Analytics</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">API</div>
              <div className="text-gray-400 text-sm md:text-base">Integration Access</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Training</div>
              <div className="text-gray-400 text-sm md:text-base">Dedicated Support</div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Built For Training Programs
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to run successful workforce development programs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-lg">
                    <feature.icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Programs Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <TrendingUp className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Your Program?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start achieving better outcomes with LoopBaan&apos;s workforce development platform.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                Partner With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
